import { useMemo, useState } from 'react';
import type { CatalogCategoryOption } from '@vce/shared';
import { cn } from '@/lib/utils';

interface CategoryIngestPickerProps {
  options: CatalogCategoryOption[];
  selectedIds: number[];
  onChange(ids: number[]): void;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * Multi-select pesquisável por nome/path da árvore VTEX.
 * Vazio = catálogo inteiro; seleção inclui subcategorias no worker.
 */
export function CategoryIngestPicker({
  options,
  selectedIds,
  onChange,
  disabled,
  isLoading,
  isError,
}: CategoryIngestPickerProps) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options.slice(0, 80);
    return options
      .filter((item) => {
        const hay = `${item.name} ${item.path.join(' ')}`.toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 80);
  }, [options, query]);

  function toggle(id: number): void {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next].sort((a, b) => a - b));
  }

  function clear(): void {
    onChange([]);
    setQuery('');
  }

  const selectedLabels = options.filter((item) => selected.has(item.id));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          disabled={disabled || isLoading}
          placeholder="Buscar categoria (ex.: masculino)…"
          onChange={(event) => setQuery(event.target.value)}
          className={cn(
            'min-h-9 min-w-[12rem] flex-1 rounded-[10px] border border-[var(--color-hairline)]',
            'bg-[var(--color-surface-card)] px-3 text-sm text-[var(--color-ink)]',
            'placeholder:text-[var(--color-muted)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
            'disabled:opacity-60',
          )}
        />
        {selectedIds.length > 0 ? (
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            className="text-xs font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)]"
          >
            Limpar ({selectedIds.length})
          </button>
        ) : null}
      </div>

      {selectedLabels.length > 0 ? (
        <p className="text-[11px] text-[var(--color-body)]">
          Selecionadas:{' '}
          {selectedLabels.map((item) => item.path.join(' › ')).join(' · ')}
          {' · '}
          <span className="text-[var(--color-muted)]">subcategorias inclusas na ingestão</span>
        </p>
      ) : (
        <p className="text-[11px] text-[var(--color-muted)]">
          Vazio = catálogo inteiro. Com categoria, inclui subcategorias.
        </p>
      )}

      {isError ? (
        <p className="text-xs text-[var(--color-danger)]">
          Não foi possível carregar as categorias da VTEX.
        </p>
      ) : null}

      {isLoading ? (
        <p className="text-xs text-[var(--color-muted)]">Carregando árvore de categorias…</p>
      ) : (
        <ul
          className={cn(
            'max-h-40 overflow-y-auto rounded-[10px] border border-[var(--color-hairline)]',
            'bg-[var(--color-surface-soft)]',
          )}
          role="listbox"
          aria-label="Categorias"
          aria-multiselectable
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-xs text-[var(--color-muted)]">Nenhuma categoria.</li>
          ) : (
            filtered.map((item) => {
              const checked = selected.has(item.id);
              return (
                <li key={item.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-2 px-3 py-1.5 text-xs',
                      'hover:bg-[var(--color-surface-elevated)]',
                      checked ? 'bg-[var(--color-surface-card)]' : null,
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(item.id)}
                    />
                    <span className="min-w-0">
                      <span className="font-medium text-[var(--color-ink)]">{item.name}</span>
                      <span className="mt-0.5 block truncate text-[10px] text-[var(--color-muted)]">
                        {item.path.join(' › ')}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
